import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ICU from "i18next-icu";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/hi";

import { DEFAULT_NAMESPACE, FALLBACK_LOCALE, NAMESPACES, SUPPORTED_LOCALES, SupportedLocale } from "./config";
import { detectInitialLocale, persistLocale } from "./detect";

import enCommon from "./locales/en/common.json";
import enValidation from "./locales/en/validation.json";
import enErrors from "./locales/en/errors.json";
import enSnackbar from "./locales/en/snackbar.json";
import enAuth from "./locales/en/auth.json";
import enAccount from "./locales/en/account.json";
import enBilling from "./locales/en/billing.json";
import enHome from "./locales/en/home.json";
import enPhotoshoot from "./locales/en/photoshoot.json";
import enDesignCanvas from "./locales/en/design-canvas.json";
import enTechPack from "./locales/en/tech-pack.json";
import enTraining from "./locales/en/training.json";
import enTrends from "./locales/en/trends.json";
import enBrandDna from "./locales/en/brand-dna.json";
import enAssets from "./locales/en/assets.json";
import enColorPalettes from "./locales/en/color-palettes.json";
import enBrandKits from "./locales/en/brand-kits.json";
import enEditDesign from "./locales/en/edit-design.json";
import enMarketplace from "./locales/en/marketplace.json";
import enBulkProcess from "./locales/en/bulk-process.json";
import enAcademy from "./locales/en/academy.json";
import enCollectionRangePlan from "./locales/en/collection-range-plan.json";
import enNotifications from "./locales/en/notifications.json";
import enMiro from "./locales/en/miro.json";

import trCommon from "./locales/tr/common.json";
import trValidation from "./locales/tr/validation.json";
import trErrors from "./locales/tr/errors.json";
import trSnackbar from "./locales/tr/snackbar.json";
import trAuth from "./locales/tr/auth.json";
import trAccount from "./locales/tr/account.json";
import trBilling from "./locales/tr/billing.json";
import trHome from "./locales/tr/home.json";
import trPhotoshoot from "./locales/tr/photoshoot.json";
import trDesignCanvas from "./locales/tr/design-canvas.json";
import trTechPack from "./locales/tr/tech-pack.json";
import trTraining from "./locales/tr/training.json";
import trTrends from "./locales/tr/trends.json";
import trBrandDna from "./locales/tr/brand-dna.json";
import trAssets from "./locales/tr/assets.json";
import trColorPalettes from "./locales/tr/color-palettes.json";
import trBrandKits from "./locales/tr/brand-kits.json";
import trEditDesign from "./locales/tr/edit-design.json";
import trMarketplace from "./locales/tr/marketplace.json";
import trBulkProcess from "./locales/tr/bulk-process.json";
import trAcademy from "./locales/tr/academy.json";
import trCollectionRangePlan from "./locales/tr/collection-range-plan.json";
import trNotifications from "./locales/tr/notifications.json";
import trMiro from "./locales/tr/miro.json";

import esCommon from "./locales/es/common.json";
import esValidation from "./locales/es/validation.json";
import esErrors from "./locales/es/errors.json";
import esSnackbar from "./locales/es/snackbar.json";
import esAuth from "./locales/es/auth.json";
import esAccount from "./locales/es/account.json";
import esBilling from "./locales/es/billing.json";
import esHome from "./locales/es/home.json";
import esPhotoshoot from "./locales/es/photoshoot.json";
import esDesignCanvas from "./locales/es/design-canvas.json";
import esTechPack from "./locales/es/tech-pack.json";
import esTraining from "./locales/es/training.json";
import esTrends from "./locales/es/trends.json";
import esBrandDna from "./locales/es/brand-dna.json";
import esAssets from "./locales/es/assets.json";
import esColorPalettes from "./locales/es/color-palettes.json";
import esBrandKits from "./locales/es/brand-kits.json";
import esEditDesign from "./locales/es/edit-design.json";
import esMarketplace from "./locales/es/marketplace.json";
import esBulkProcess from "./locales/es/bulk-process.json";
import esAcademy from "./locales/es/academy.json";
import esCollectionRangePlan from "./locales/es/collection-range-plan.json";
import esNotifications from "./locales/es/notifications.json";
import esMiro from "./locales/es/miro.json";

import ptCommon from "./locales/pt/common.json";
import ptValidation from "./locales/pt/validation.json";
import ptErrors from "./locales/pt/errors.json";
import ptSnackbar from "./locales/pt/snackbar.json";
import ptAuth from "./locales/pt/auth.json";
import ptAccount from "./locales/pt/account.json";
import ptBilling from "./locales/pt/billing.json";
import ptHome from "./locales/pt/home.json";
import ptPhotoshoot from "./locales/pt/photoshoot.json";
import ptDesignCanvas from "./locales/pt/design-canvas.json";
import ptTechPack from "./locales/pt/tech-pack.json";
import ptTraining from "./locales/pt/training.json";
import ptTrends from "./locales/pt/trends.json";
import ptBrandDna from "./locales/pt/brand-dna.json";
import ptAssets from "./locales/pt/assets.json";
import ptColorPalettes from "./locales/pt/color-palettes.json";
import ptBrandKits from "./locales/pt/brand-kits.json";
import ptEditDesign from "./locales/pt/edit-design.json";
import ptMarketplace from "./locales/pt/marketplace.json";
import ptBulkProcess from "./locales/pt/bulk-process.json";
import ptAcademy from "./locales/pt/academy.json";
import ptCollectionRangePlan from "./locales/pt/collection-range-plan.json";
import ptNotifications from "./locales/pt/notifications.json";
import ptMiro from "./locales/pt/miro.json";

import itCommon from "./locales/it/common.json";
import itValidation from "./locales/it/validation.json";
import itErrors from "./locales/it/errors.json";
import itSnackbar from "./locales/it/snackbar.json";
import itAuth from "./locales/it/auth.json";
import itAccount from "./locales/it/account.json";
import itBilling from "./locales/it/billing.json";
import itHome from "./locales/it/home.json";
import itPhotoshoot from "./locales/it/photoshoot.json";
import itDesignCanvas from "./locales/it/design-canvas.json";
import itTechPack from "./locales/it/tech-pack.json";
import itTraining from "./locales/it/training.json";
import itTrends from "./locales/it/trends.json";
import itBrandDna from "./locales/it/brand-dna.json";
import itAssets from "./locales/it/assets.json";
import itColorPalettes from "./locales/it/color-palettes.json";
import itBrandKits from "./locales/it/brand-kits.json";
import itEditDesign from "./locales/it/edit-design.json";
import itMarketplace from "./locales/it/marketplace.json";
import itBulkProcess from "./locales/it/bulk-process.json";
import itAcademy from "./locales/it/academy.json";
import itCollectionRangePlan from "./locales/it/collection-range-plan.json";
import itNotifications from "./locales/it/notifications.json";
import itMiro from "./locales/it/miro.json";

import frCommon from "./locales/fr/common.json";
import frValidation from "./locales/fr/validation.json";
import frErrors from "./locales/fr/errors.json";
import frSnackbar from "./locales/fr/snackbar.json";
import frAuth from "./locales/fr/auth.json";
import frAccount from "./locales/fr/account.json";
import frBilling from "./locales/fr/billing.json";
import frHome from "./locales/fr/home.json";
import frPhotoshoot from "./locales/fr/photoshoot.json";
import frDesignCanvas from "./locales/fr/design-canvas.json";
import frTechPack from "./locales/fr/tech-pack.json";
import frTraining from "./locales/fr/training.json";
import frTrends from "./locales/fr/trends.json";
import frBrandDna from "./locales/fr/brand-dna.json";
import frAssets from "./locales/fr/assets.json";
import frColorPalettes from "./locales/fr/color-palettes.json";
import frBrandKits from "./locales/fr/brand-kits.json";
import frEditDesign from "./locales/fr/edit-design.json";
import frMarketplace from "./locales/fr/marketplace.json";
import frBulkProcess from "./locales/fr/bulk-process.json";
import frAcademy from "./locales/fr/academy.json";
import frCollectionRangePlan from "./locales/fr/collection-range-plan.json";
import frNotifications from "./locales/fr/notifications.json";
import frMiro from "./locales/fr/miro.json";

import hiCommon from "./locales/hi/common.json";
import hiValidation from "./locales/hi/validation.json";
import hiErrors from "./locales/hi/errors.json";
import hiNotifications from "./locales/hi/notifications.json";
import hiMiro from "./locales/hi/miro.json";
import hiSnackbar from "./locales/hi/snackbar.json";
import hiAuth from "./locales/hi/auth.json";
import hiAccount from "./locales/hi/account.json";
import hiBilling from "./locales/hi/billing.json";
import hiHome from "./locales/hi/home.json";
import hiPhotoshoot from "./locales/hi/photoshoot.json";
import hiDesignCanvas from "./locales/hi/design-canvas.json";
import hiTechPack from "./locales/hi/tech-pack.json";
import hiTraining from "./locales/hi/training.json";
import hiTrends from "./locales/hi/trends.json";
import hiBrandDna from "./locales/hi/brand-dna.json";
import hiAssets from "./locales/hi/assets.json";
import hiEditDesign from "./locales/hi/edit-design.json";
import hiMarketplace from "./locales/hi/marketplace.json";
import hiBulkProcess from "./locales/hi/bulk-process.json";
import hiAcademy from "./locales/hi/academy.json";
import hiCollectionRangePlan from "./locales/hi/collection-range-plan.json";
import hiColorPalettes from "./locales/hi/color-palettes.json";
import hiBrandKits from "./locales/hi/brand-kits.json";

// Exported for `resources.test.ts`, which turns the dev-only drift guard below into a build
// failure. The guard console.errors; a console nobody reads is how `hi` shipped without the
// `notifications` namespace, rendering raw keys for every notification in Hindi.
export const resources = {
  en: {
    common: enCommon,
    validation: enValidation,
    errors: enErrors,
    snackbar: enSnackbar,
    auth: enAuth,
    account: enAccount,
    billing: enBilling,
    home: enHome,
    photoshoot: enPhotoshoot,
    "design-canvas": enDesignCanvas,
    "tech-pack": enTechPack,
    training: enTraining,
    trends: enTrends,
    "brand-dna": enBrandDna,
    assets: enAssets,
    "color-palettes": enColorPalettes,
    "brand-kits": enBrandKits,
    "edit-design": enEditDesign,
    marketplace: enMarketplace,
    "bulk-process": enBulkProcess,
    academy: enAcademy,
    "collection-range-plan": enCollectionRangePlan,
    notifications: enNotifications,
    miro: enMiro,
  },
  tr: {
    common: trCommon,
    validation: trValidation,
    errors: trErrors,
    snackbar: trSnackbar,
    auth: trAuth,
    account: trAccount,
    billing: trBilling,
    home: trHome,
    photoshoot: trPhotoshoot,
    "design-canvas": trDesignCanvas,
    "tech-pack": trTechPack,
    training: trTraining,
    trends: trTrends,
    "brand-dna": trBrandDna,
    assets: trAssets,
    "color-palettes": trColorPalettes,
    "brand-kits": trBrandKits,
    "edit-design": trEditDesign,
    marketplace: trMarketplace,
    "bulk-process": trBulkProcess,
    academy: trAcademy,
    "collection-range-plan": trCollectionRangePlan,
    notifications: trNotifications,
    miro: trMiro,
  },
  es: {
    common: esCommon,
    validation: esValidation,
    errors: esErrors,
    snackbar: esSnackbar,
    auth: esAuth,
    account: esAccount,
    billing: esBilling,
    home: esHome,
    photoshoot: esPhotoshoot,
    "design-canvas": esDesignCanvas,
    "tech-pack": esTechPack,
    training: esTraining,
    trends: esTrends,
    "brand-dna": esBrandDna,
    assets: esAssets,
    "color-palettes": esColorPalettes,
    "brand-kits": esBrandKits,
    "edit-design": esEditDesign,
    marketplace: esMarketplace,
    "bulk-process": esBulkProcess,
    academy: esAcademy,
    "collection-range-plan": esCollectionRangePlan,
    notifications: esNotifications,
    miro: esMiro,
  },
  pt: {
    common: ptCommon,
    validation: ptValidation,
    errors: ptErrors,
    snackbar: ptSnackbar,
    auth: ptAuth,
    account: ptAccount,
    billing: ptBilling,
    home: ptHome,
    photoshoot: ptPhotoshoot,
    "design-canvas": ptDesignCanvas,
    "tech-pack": ptTechPack,
    training: ptTraining,
    trends: ptTrends,
    "brand-dna": ptBrandDna,
    assets: ptAssets,
    "color-palettes": ptColorPalettes,
    "brand-kits": ptBrandKits,
    "edit-design": ptEditDesign,
    marketplace: ptMarketplace,
    "bulk-process": ptBulkProcess,
    academy: ptAcademy,
    "collection-range-plan": ptCollectionRangePlan,
    notifications: ptNotifications,
    miro: ptMiro,
  },
  it: {
    common: itCommon,
    validation: itValidation,
    errors: itErrors,
    snackbar: itSnackbar,
    auth: itAuth,
    account: itAccount,
    billing: itBilling,
    home: itHome,
    photoshoot: itPhotoshoot,
    "design-canvas": itDesignCanvas,
    "tech-pack": itTechPack,
    training: itTraining,
    trends: itTrends,
    "brand-dna": itBrandDna,
    assets: itAssets,
    "color-palettes": itColorPalettes,
    "brand-kits": itBrandKits,
    "edit-design": itEditDesign,
    marketplace: itMarketplace,
    "bulk-process": itBulkProcess,
    academy: itAcademy,
    "collection-range-plan": itCollectionRangePlan,
    notifications: itNotifications,
    miro: itMiro,
  },
  fr: {
    common: frCommon,
    validation: frValidation,
    errors: frErrors,
    snackbar: frSnackbar,
    auth: frAuth,
    account: frAccount,
    billing: frBilling,
    home: frHome,
    photoshoot: frPhotoshoot,
    "design-canvas": frDesignCanvas,
    "tech-pack": frTechPack,
    training: frTraining,
    trends: frTrends,
    "brand-dna": frBrandDna,
    assets: frAssets,
    "color-palettes": frColorPalettes,
    "brand-kits": frBrandKits,
    "edit-design": frEditDesign,
    marketplace: frMarketplace,
    "bulk-process": frBulkProcess,
    academy: frAcademy,
    "collection-range-plan": frCollectionRangePlan,
    notifications: frNotifications,
    miro: frMiro,
  },
  hi: {
    common: hiCommon,
    validation: hiValidation,
    errors: hiErrors,
    notifications: hiNotifications,
    miro: hiMiro,
    snackbar: hiSnackbar,
    auth: hiAuth,
    account: hiAccount,
    billing: hiBilling,
    home: hiHome,
    photoshoot: hiPhotoshoot,
    "design-canvas": hiDesignCanvas,
    "tech-pack": hiTechPack,
    training: hiTraining,
    trends: hiTrends,
    "brand-dna": hiBrandDna,
    assets: hiAssets,
    "edit-design": hiEditDesign,
    marketplace: hiMarketplace,
    "bulk-process": hiBulkProcess,
    academy: hiAcademy,
    "collection-range-plan": hiCollectionRangePlan,
    "color-palettes": hiColorPalettes,
    "brand-kits": hiBrandKits,
  },
} as const;

// Dev-only drift guard: the `resources` map above is hand-maintained (one import + block per
// locale × namespace). Fail loudly in development if it falls out of sync with the single sources
// of truth (SUPPORTED_LOCALES, NAMESPACES) so a missing locale/namespace is caught at boot rather
// than surfacing as a silent EN fallback. Non-throwing — never breaks the app at runtime.
if (process.env.NODE_ENV !== "production") {
  const expectedNs = [...NAMESPACES].sort().join(",");
  for (const locale of SUPPORTED_LOCALES) {
    const block = (resources as Record<string, Record<string, unknown>>)[locale];
    if (!block) {
      console.error(`[i18n] resources is missing locale "${locale}" declared in SUPPORTED_LOCALES.`);
      continue;
    }
    const actualNs = Object.keys(block).sort().join(",");
    if (actualNs !== expectedNs) {
      console.error(`[i18n] resources["${locale}"] namespaces drifted from NAMESPACES.\n  expected: ${expectedNs}\n  actual:   ${actualNs}`);
    }
  }
}

const initialLocale: SupportedLocale = detectInitialLocale();

i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLocale,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    ns: NAMESPACES as unknown as string[],
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    returnNull: false,
  });

dayjs.locale(initialLocale);

i18n.on("languageChanged", (lng) => {
  dayjs.locale(lng);
  persistLocale(lng as SupportedLocale);
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});

if (typeof document !== "undefined") {
  document.documentElement.lang = initialLocale;
}

export default i18n;
